import {
  Modal,
  ModalProvider,
  PrimaryButton,
  SecondaryButton,
} from '@joor/design-system'
import styled from 'styled-components/macro'

import { OrderCheckMinimums, OrderStatus } from '__generated__/atlas-types'

import usePortal from 'hooks/usePortal'

import { MinimumCheck } from './MinimumCheck'

function OrderMinRequirementsModal({
  onContinue,
  onEditQuantities,
  onClose,
  applyRestrictions = false,
  isLoading,
  checkMinimums,
}: Props) {
  const Portal = usePortal()
  const bodyText = getBodyText(applyRestrictions)
  return (
    <Portal>
      <ModalProvider>
        <Modal
          isOpen
          title={{ text: 'Minimums not met', iconName: 'warning' }}
          size="medium"
          onClose={() => onClose?.()}
        >
          <Modal.Content>
            <ContentArea>
              <div>{bodyText}</div>
              {!!checkMinimums && (
                <>
                  <div>Minimum Status:</div>
                  <ChecksArea>
                    <MinimumCheck
                      isMet={checkMinimums.orderAmount}
                      label={'Order Amount Minimum'}
                    />
                    <MinimumCheck
                      isMet={checkMinimums.orderQuantity}
                      label={'Order Unit Minimum'}
                    />
                    <MinimumCheck
                      isMet={checkMinimums.productMinimums}
                      label={'Individual Style Minimums'}
                    />
                    <MinimumCheck
                      isMet={checkMinimums.colorMinimums}
                      label={'Color Unit Minimums within Styles'}
                    />
                  </ChecksArea>
                </>
              )}
            </ContentArea>
          </Modal.Content>
          <Modal.Actions>
            <ActionsArea>
              {applyRestrictions ? (
                <PrimaryButton onClick={onEditQuantities}>
                  Edit Quantities
                </PrimaryButton>
              ) : (
                <>
                  <SecondaryButton
                    onClick={onEditQuantities}
                    disabled={isLoading}
                  >
                    Edit Quantities
                  </SecondaryButton>
                  <PrimaryButton onClick={onContinue} loading={isLoading}>
                    Continue
                  </PrimaryButton>
                </>
              )}
            </ActionsArea>
          </Modal.Actions>
        </Modal>
      </ModalProvider>
    </Portal>
  )
}

export type Props = {
  onContinue: () => void
  onEditQuantities: () => void
  isLoading: boolean
  onClose?: () => void
  applyRestrictions?: boolean
  checkMinimums?: OrderCheckMinimums
}

const ActionsArea = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 10px;
`

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ChecksArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

type ShouldModalOpenParams = {
  isProBrand: boolean
  isLiteRetailer: boolean
  newStatus: OrderStatus
  areMinimumsMet: boolean
}
export const shouldModalOpen = ({
  isProBrand,
  isLiteRetailer,
  newStatus,
  areMinimumsMet,
}: ShouldModalOpenParams) => {
  const applicableBrandStatuses = [
    OrderStatus.PENDING,
    OrderStatus.APPROVED,
    OrderStatus.SHIPPED,
  ]
  const applicableRetailerStatuses = [OrderStatus.PENDING]
  if (isProBrand) {
    return !areMinimumsMet && applicableBrandStatuses.includes(newStatus)
  }
  if (isLiteRetailer) {
    return !areMinimumsMet && applicableRetailerStatuses.includes(newStatus)
  }
}

const getBodyText = (restricted: boolean) => {
  if (restricted) {
    return (
      'This order does not meet your some or all of your specified minimums' +
      ' and cannot be updated until it does. Edit quantities by clicking below.'
    )
  }
  return (
    'This order does not meet your some or all of your specified minimums. ' +
    'Would you like to approve with the current quantities?'
  )
}

export default OrderMinRequirementsModal
